import { Delete, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { useAdminStore } from "../../../context/admin-store";
import { getAllEvents } from "../../../services/event.service";
import { parseDate } from "../../../utils/parseDate";
import { deleteReceipt, getReceipts } from "../../UserDashboard/Receipts";
import { useToast } from "../../../ui-components/ToastProvider";
import { useConfirm } from "../../../ui-components/ConfirmProvider";

export default function Receipts() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const cols = isXs ? 1 : isMd ? 2 : 4;

  const users = useAdminStore((state) => state.users);
  const [events, setEvents] = React.useState<any[]>([]);
  const [receiptsList, setReceiptsList] = React.useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = React.useState<number | null>(
    null
  );
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const syncEvents = React.useCallback(async () => {
    const ev = await getAllEvents();
    setEvents(ev || []);
    // Evita depender de selectedEventId usando atualização funcional
    if (ev && ev.length) {
      setSelectedEventId((prev) => (prev === null ? ev[0].id : prev));
    }
  }, []);

  const syncReceipts = React.useCallback(async () => {
    const rec = await getReceipts();
    setReceiptsList(rec || []);
  }, []);

  React.useEffect(() => {
    syncEvents();
    syncReceipts();
  }, [syncEvents, syncReceipts]);

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: "Excluir comprovante",
      description:
        "Você realmente deseja excluir este comprovante? Esta ação pode ser irreversível!",
      confirmText: "Excluir",
      cancelText: "Cancelar",
    });
    if (!ok) return;
    await deleteReceipt(id);
    await syncReceipts();
    showToast("Comprovante excluído com sucesso!", { severity: "success" });
  };

  const filteredEvents = React.useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev: any) => {
      const tabLabel = `${ev.name} - ${parseDate(ev.starts_at)}`;
      return tabLabel.toLowerCase().includes(q);
    });
  }, [events, eventSearch]);

  const eventTabs = (filteredEvents || []).map((ev: any) => (
    <Tab
      key={ev.id}
      value={ev.id}
      label={`${ev.name} - ${parseDate(ev.starts_at)}`}
      sx={{ textTransform: "none", fontWeight: 600 }}
    />
  ));

  const receiptsForSelectedEvent = receiptsList.filter((r: any) =>
    selectedEventId ? r.event_id === selectedEventId : false
  );

  const groupedByUser = receiptsForSelectedEvent.reduce(
    (acc: Record<number, any[]>, item: any) => {
      acc[item.user_id] = acc[item.user_id] || [];
      acc[item.user_id].push(item);
      return acc;
    },
    {}
  );

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, mb: 2 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography
            sx={{ fontWeight: 700, color: theme.palette.text.primary }}
          >
            Comprovantes
          </Typography>
          <Box sx={{ ml: "auto", width: { xs: "100%", sm: 320 } }}>
            <TextField
              placeholder="Pesquisar eventos"
              size="small"
              fullWidth
              value={eventSearch}
              onChange={(e) => setEventSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        <Tabs
          value={selectedEventId}
          onChange={(_, val) => setSelectedEventId(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, minHeight: 40 }}
        >
          {eventTabs}
        </Tabs>
      </Paper>

      <Typography
        sx={{
          display: "inline-block",
          fontSize: "0.9rem",
          mb: 2,
          background: "#f1f5f9",
          border: "1px solid #e2e8f0",
          borderRadius: "9999px",
          color: "#64748b",
          px: 2,
          py: 0.5,
        }}
      >
        Lista de Comprovantes
      </Typography>

      {Object.keys(groupedByUser).length === 0 && (
        <Typography sx={{ color: theme.palette.text.secondary }}>
          Nenhum comprovante para o evento selecionado.
        </Typography>
      )}

      {Object.entries(groupedByUser).map(([userId, items]) => {
        const userName =
          (users.find((u: any) => u.id === Number(userId)) || {}).name ||
          "Usuário";
        return (
          <Box key={userId} sx={{ mb: 3 }}>
            <Typography
              sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
            >
              Usuário: {userName}
            </Typography>
            <ImageList cols={cols} gap={12} sx={{ m: 0 }}>
              {(items as any[]).map((item) => (
                <a
                  key={item.id}
                  target={"_blank"}
                  rel="noreferrer"
                  href={item.url}
                >
                  <ImageListItem
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      transition: "transform 0.15s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(36,50,77,0.25)",
                      },
                    }}
                  >
                    <img
                      src={
                        item.file_type.includes("image/")
                          ? item.url
                          : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAbFBMVEX///8AAABLS0vc3NwwMDDo6Oi0tLTCwsJ0dHSGhoY9PT0/Pz/09PQcHBx4eHg4ODhVVVUUFBTIyMiXl5eqqqqhoaFtbW3S0tLOzs4uLi7i4uJlZWUlJSVfX19QUFCsrKwYGBiAgIBFRUW6urrFevtnAAADDElEQVR4nO3d61IaQRRF4eGiEMMtQAAxaozv/46JSf50M1xK9j5ntNb3ANOs6p7pES27aQAAAAAAAAAAAD685WrWnw9F7vvZObXlQ0/sLjupsP6m7utW4n5o6OtS4ndP3x9fs9P+2dgCO5K4MAZ2ItE5g2/S70XfdiRxIE9MHuhmraJ7iTeRgSmJv6IKcxLHAUF5iU+1h/k6XV7c51jz+akxOpTTLfXX/LonZ2SuKs+xERwzePProx9sVpRghk8+XROSBwXH+BJcs1T+0/8Qn0qxn+WXPPkDhs+i339Ij3zDhE9i2XhQHLNM29JwbOYUBg8ixmFsbPoLxzPc2fRX/ilafmWMnAWIwonLT++xCVGFDaTzFkMKWwm07zEmMLWxKDHTVBh4ixGFebNYlhhM/mZM4txhVkLNbCwddOYS0Y8JbKwdRbtiaGFKVt/bGHGvRhcmDCL0YXxm0Z4YXhifGHT3Icm+gvvZuPS7OBXJdbElO9p2tj2xc4U9oaSsQ91p9C1UDtU2HuUjF7rUqHnbzUpfA8KKdSi8D0opFDLUXgzutT6gxZebv/pCwcUClDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaHC5y8sT3jcWcY4rjwFVXOCZq08N35tGeO48j9DLixjvASsk+N+FaO/WMaozpKNXabVUb2eFbQtB5GcB3ypSTW25hTUA9UoijOdL7SrD2QxjfNQDdNbXH0u9yW2t/164I2pcFkPlMb2DGg5EyWF63yLpnnNTvtvZCtsWs56TeA8KXBwfvgAe2NhveunML8vbrL7ejNvYP3+Hc9zwkwhdxbH/sDcezHoZ7ZB1s4/tz5FC+uWowntps9hfW9GB6/hZptlaN9fy9WsPx/6zfuzVfS3QgAAAAAAAAAAAAa/ARXQN87ZDmHrAAAAAElFTkSuQmCC"
                      }
                      alt={item.title}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: 180,
                        objectFit: "cover",
                        background: "#f8fafc",
                      }}
                    />
                    <ImageListItemBar
                      title={item.name}
                      subtitle={item["file_type"]}
                      sx={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.15))",
                        ".MuiImageListItemBar-title": { fontWeight: 600 },
                        ".MuiImageListItemBar-subtitle": { opacity: 0.85 },
                      }}
                      actionIcon={
                        <IconButton
                          sx={{ color: "#fff" }}
                          aria-label={`delete ${item.title}`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleDelete(item.id);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                </a>
              ))}
            </ImageList>
          </Box>
        );
      })}
    </Box>
  );
}
